import React from "react"
import DataTable from "@swipesense/data-table"
import { Column } from "react-virtualized"
import "./AssetAlertsListTable.scss"
import FontAwesome from "react-fontawesome"
import cloudinary from "cloudinary-core"
import PropTypes from "prop-types"
import Loading from "../Loading"

const RENTAL_POLICY_TEXT_MAPPING = {
  exclude: null,
  include: "Rentals Included",
  only: "Rentals Only",
}

// Renders "Nicolas Cage, Dalai Lama + 5 others"
const subscriptionsRenderer = ({ cellData }) => {
  return [
    <span key="number">
      {cellData
        .slice(0, 2)
        .map(subscription => subscription.user.name)
        .join(", ")}
    </span>,
    cellData.length > 2 && (
      <span key="others" className="others">
        {" "}
        + {cellData.length - 2} others
      </span>
    ),
  ]
}

const rentalPolicyRenderer = ({ cellData }) => {
  const text = RENTAL_POLICY_TEXT_MAPPING[cellData]
  if (text) {
    return [
      <FontAwesome key="tag" className="rental-tag" name={"tag"} />,
      <span key="text" className="rental-text">
        {text}
      </span>,
    ]
  }
}

const DEVICES_TO_SHOW = 2
const CLOUDINARY = new cloudinary.Cloudinary({
  cloud_name: "swipesense-inc",
})
const PHOTO_FALLBACK_URL = "/icons/fallback.svg"

const devicesRenderer = ({ cellData }) => {
  const output = cellData
    .slice()
    .filter(assetAlert => assetAlert.assets.length > 0)
    .sort((a, b) => (a.assets[0].imageId ? -1 : 1))
    .slice(0, DEVICES_TO_SHOW)
    .map(device => device.assets[0].imageId)
    .map((imageId, i) => {
      const photoUrl = imageId
        ? CLOUDINARY.url(imageId, {
            width: 36,
            height: 36,
            crop: "fill",
          })
        : PHOTO_FALLBACK_URL
      return (
        <div key={i} className="image-wrapper">
          <img className="asset-image" alt="asset" src={photoUrl} />
          <div className="asset-image overlay shadow" />
        </div>
      )
    })

  const additionalDevices = cellData.length - DEVICES_TO_SHOW
  if (additionalDevices > 0) {
    output.push(
      <div key="more" className="image-wrapper">
        <div className="asset-image more-background" />
        <div className="asset-image overlay shadow" />
        <div className="asset-image overlay number">+{additionalDevices}</div>
      </div>
    )
  }
  return output
}

const AssetAlertsListTable = ({ loading, assetAlerts }) => (
  <div className="AssetAlertsListTable">
    <DataTable
      data={assetAlerts}
      rowHeight={60}
      rowCount={assetAlerts.length}
      headerClassName="table-header"
      loading={loading}
      noRowsMessage="No alerts found. Ask your facility administrator to create your first alert!"
      loadingComponent={<Loading />}
    >
      <Column
        width={20}
        dataKey="devices"
        className="devices"
        label="Assets"
        disableSort={true}
        cellRenderer={devicesRenderer}
      />
      <Column
        width={200}
        dataKey="ownedBy"
        className="ownedBy"
        label="Ownership"
        disableSort={true}
        cellRenderer={({ cellData }) =>
          cellData ? cellData.name : "All Departments"
        }
      />
      <Column
        width={100}
        dataKey="rentalPolicy"
        className="rentalPolicy"
        disableSort={true}
        cellRenderer={rentalPolicyRenderer}
      />
      <Column
        width={10}
        dataKey="locations"
        className="locations"
        label="Locations"
        disableSort={true}
        cellRenderer={({ cellData }) =>
          cellData.map(loc => loc.name).join(", ")
        }
      />
      <Column
        width={250}
        dataKey="subscriptions"
        className="subscriptions"
        label="People Notified"
        disableSort={true}
        cellRenderer={subscriptionsRenderer}
      />
    </DataTable>
  </div>
)

AssetAlertsListTable.propTypes = {
  loading: PropTypes.bool,
  assetAlerts: PropTypes.arrayOf(
    PropTypes.shape({
      rentalPolicy: PropTypes.string.isRequired,
      devices: PropTypes.arrayOf(
        PropTypes.shape({
          assets: PropTypes.arrayOf(
            PropTypes.shape({ imageId: PropTypes.string })
          ).isRequired,
        })
      ).isRequired,
      ownedBy: PropTypes.shape({ name: PropTypes.string.isRequired }),
      locations: PropTypes.array.isRequired, // Doesn't matter what's in here, we just need the length
      subscriptions: PropTypes.arrayOf(
        PropTypes.shape({
          user: PropTypes.shape({ name: PropTypes.string.isRequired }),
        })
      ).isRequired,
    })
  ).isRequired,
}

AssetAlertsListTable.defaultProps = {
  assetAlerts: [],
  loading: false,
}

export default AssetAlertsListTable
